import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Clock, Send, HelpCircle } from 'lucide-react';
import { Layout, useLanguage } from '@/components/Layout';
import { CONTACT_INFO, translations } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: 'spring', stiffness: 300, damping: 35 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const staggerItem = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

export default function Contact() {
  const { language } = useLanguage();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const faqData = [
    {
      question: {
        fr: 'Quels documents sont nécessaires pour le dédouanement ?',
        en: 'What documents are required for customs clearance?',
        es: '¿Qué documentos se requieren para el despacho aduanero?',
        zh: '清关需要哪些文件？',
        ar: 'ما هي المستندات المطلوبة للتخليص الجمركي؟'
      },
      answer: {
        fr: 'Les documents essentiels incluent : facture commerciale, liste de colisage, certificat d\'origine, documents de transport (connaissement/lettre de transport aérien), et déclaration en douane. Des documents supplémentaires peuvent être requis selon la nature des marchandises.',
        en: 'Essential documents include: commercial invoice, packing list, certificate of origin, transport documents (bill of lading/air waybill), and customs declaration. Additional documents may be required depending on the nature of goods.',
        es: 'Los documentos esenciales incluyen: factura comercial, lista de empaque, certificado de origen, documentos de transporte (conocimiento de embarque/guía aérea) y declaración aduanera. Pueden requerirse documentos adicionales según la naturaleza de las mercancías.',
        zh: '必要文件包括：商业发票、装箱单、原产地证书、运输文件（提单/空运单）和海关申报单。根据货物性质可能需要额外文件。',
        ar: 'تشمل المستندات الأساسية: الفاتورة التجارية، قائمة التعبئة، شهادة المنشأ، مستندات النقل (بوليصة الشحن/بوليصة الشحن الجوي)، والإقرار الجمركي. قد تكون هناك حاجة إلى مستندات إضافية حسب طبيعة البضائع.'
      }
    },
    {
      question: {
        fr: 'Combien de temps prend le processus de dédouanement ?',
        en: 'How long does the customs clearance process take?',
        es: '¿Cuánto tiempo toma el proceso de despacho aduanero?',
        zh: '清关流程需要多长时间？',
        ar: 'كم من الوقت تستغرق عملية التخليص الجمركي؟'
      },
      answer: {
        fr: 'Le délai varie selon le pays et le type de marchandises, généralement entre 1 à 5 jours ouvrables. Avec notre expertise, nous optimisons ce processus pour minimiser les délais et éviter les retards.',
        en: 'The timeframe varies by country and type of goods, typically between 1 to 5 business days. With our expertise, we optimize this process to minimize delays and avoid holdups.',
        es: 'El plazo varía según el país y el tipo de mercancías, típicamente entre 1 a 5 días hábiles. Con nuestra experiencia, optimizamos este proceso para minimizar demoras y evitar retrasos.',
        zh: '时间因国家和货物类型而异，通常为1至5个工作日。凭借我们的专业知识，我们优化此流程以最大限度地减少延误。',
        ar: 'يختلف الإطار الزمني حسب البلد ونوع البضائع، عادة بين 1 إلى 5 أيام عمل. بفضل خبرتنا، نقوم بتحسين هذه العملية لتقليل التأخير وتجنب التعطيل.'
      }
    },
    {
      question: {
        fr: 'Quels sont vos tarifs pour les services douaniers ?',
        en: 'What are your rates for customs services?',
        es: '¿Cuáles son sus tarifas para los servicios aduaneros?',
        zh: '您的海关服务费率是多少？',
        ar: 'ما هي أسعاركم لخدمات الجمارك؟'
      },
      answer: {
        fr: 'Nos tarifs sont personnalisés selon le volume, la complexité et la fréquence des opérations. Contactez-nous pour un devis détaillé adapté à vos besoins spécifiques.',
        en: 'Our rates are customized based on volume, complexity, and frequency of operations. Contact us for a detailed quote tailored to your specific needs.',
        es: 'Nuestras tarifas se personalizan según el volumen, la complejidad y la frecuencia de las operaciones. Contáctenos para una cotización detallada adaptada a sus necesidades específicas.',
        zh: '我们的费率根据业务量、复杂性和频率定制。请联系我们获取针对您特定需求的详细报价。',
        ar: 'يتم تخصيص أسعارنا بناءً على الحجم والتعقيد وتكرار العمليات. اتصل بنا للحصول على عرض أسعار مفصل مصمم خصيصًا لاحتياجاتك.'
      }
    },
    {
      question: {
        fr: 'Couvrez-vous tous les types de marchandises ?',
        en: 'Do you cover all types of goods?',
        es: '¿Cubren todos los tipos de mercancías?',
        zh: '您是否涵盖所有类型的货物？',
        ar: 'هل تغطون جميع أنواع البضائع؟'
      },
      answer: {
        fr: 'Oui, nous gérons une large gamme de marchandises, y compris les produits réglementés et sensibles. Notre expertise couvre les secteurs industriel, commercial, alimentaire, pharmaceutique et bien d\'autres.',
        en: 'Yes, we handle a wide range of goods, including regulated and sensitive products. Our expertise covers industrial, commercial, food, pharmaceutical, and many other sectors.',
        es: 'Sí, manejamos una amplia gama de mercancías, incluidos productos regulados y sensibles. Nuestra experiencia cubre los sectores industrial, comercial, alimentario, farmacéutico y muchos otros.',
        zh: '是的，我们处理各种货物，包括受管制和敏感产品。我们的专业知识涵盖工业、商业、食品、制药等多个领域。',
        ar: 'نعم، نتعامل مع مجموعة واسعة من البضائع، بما في ذلك المنتجات المنظمة والحساسة. تغطي خبرتنا القطاعات الصناعية والتجارية والغذائية والصيدلانية والعديد من القطاعات الأخرى.'
      }
    },
    {
      question: {
        fr: 'Comment puis-je suivre mes expéditions ?',
        en: 'How can I track my shipments?',
        es: '¿Cómo puedo rastrear mis envíos?',
        zh: '我如何跟踪我的货物？',
        ar: 'كيف يمكنني تتبع شحناتي؟'
      },
      answer: {
        fr: 'Nous fournissons un suivi en temps réel de vos expéditions via notre système de gestion. Vous recevrez des mises à jour régulières sur l\'état de vos opérations douanières et de transit.',
        en: 'We provide real-time tracking of your shipments through our management system. You will receive regular updates on the status of your customs and transit operations.',
        es: 'Proporcionamos seguimiento en tiempo real de sus envíos a través de nuestro sistema de gestión. Recibirá actualizaciones regulares sobre el estado de sus operaciones aduaneras y de tránsito.',
        zh: '我们通过管理系统提供实时货物跟踪。您将定期收到有关海关和运输业务状态的更新。',
        ar: 'نوفر تتبعًا في الوقت الفعلي لشحناتك من خلال نظام الإدارة الخاص بنا. ستتلقى تحديثات منتظمة حول حالة عمليات الجمارك والعبور الخاصة بك.'
      }
    }
  ];

  const workingHours = {
    fr: {
      title: 'Horaires d\'ouverture',
      weekdays: 'Lundi - Vendredi',
      weekdaysTime: '9h00 - 18h00',
      weekend: 'Samedi - Dimanche',
      weekendTime: 'Fermé'
    },
    en: {
      title: 'Working Hours',
      weekdays: 'Monday - Friday',
      weekdaysTime: '9:00 AM - 6:00 PM',
      weekend: 'Saturday - Sunday',
      weekendTime: 'Closed'
    },
    es: {
      title: 'Horario de atención',
      weekdays: 'Lunes - Viernes',
      weekdaysTime: '9:00 - 18:00',
      weekend: 'Sábado - Domingo',
      weekendTime: 'Cerrado'
    },
    zh: {
      title: '工作时间',
      weekdays: '周一 - 周五',
      weekdaysTime: '9:00 - 18:00',
      weekend: '周六 - 周日',
      weekendTime: '休息'
    },
    ar: {
      title: 'ساعات العمل',
      weekdays: 'الاثنين - الجمعة',
      weekdaysTime: '9:00 صباحًا - 6:00 مساءً',
      weekend: 'السبت - الأحد',
      weekendTime: 'مغلق'
    }
  };

  return (
    <Layout>
      <div className="min-h-screen">
        <motion.section
          className="relative py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5"
          initial="initial"
          animate="animate"
          variants={fadeInUp}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <motion.h1
                className="text-5xl md:text-6xl font-bold mb-6 text-gradient"
                variants={fadeInUp}
              >
                {t.contact.title}
              </motion.h1>
              <motion.p
                className="text-xl text-muted-foreground"
                variants={fadeInUp}
              >
                {t.contact.subtitle}
              </motion.p>
            </div>
          </div>
        </motion.section>

        <section className="py-24">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 35, delay: 0.2 }}
              >
                <Card className="premium-shadow">
                  <CardHeader>
                    <CardTitle className="text-2xl">{t.contact.getInTouch}</CardTitle>
                    <CardDescription>
                      {t.contact.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium mb-2">
                          {t.contact.name}
                        </label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium mb-2">
                          {t.contact.email}
                        </label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium mb-2">
                          {t.contact.phone}
                        </label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium mb-2">
                          {t.contact.message}
                        </label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="w-full"
                        />
                      </div>
                      <Button type="submit" size="lg" className="w-full">
                        <Send className="mr-2 h-5 w-5" />
                        {t.contact.send}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>

              <motion.div
                className="space-y-6"
                variants={staggerContainer}
                initial="initial"
                animate="animate"
              >
                <motion.div variants={staggerItem}>
                  <Card className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="icon-glow p-3 rounded-xl">
                          <Phone className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{t.contact.phone}</h3>
                          <a
                            href={`tel:${CONTACT_INFO.phone}`}
                            className="text-primary hover:underline text-lg"
                          >
                            {CONTACT_INFO.phone}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={staggerItem}>
                  <Card className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="icon-glow p-3 rounded-xl">
                          <Mail className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{t.contact.email}</h3>
                          <a
                            href={`mailto:${CONTACT_INFO.email}`}
                            className="text-primary hover:underline text-lg break-all"
                          >
                            {CONTACT_INFO.email}
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>

                <motion.div variants={staggerItem}>
                  <Card className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="icon-glow p-3 rounded-xl">
                          <Clock className="h-6 w-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-4">{workingHours[language as keyof typeof workingHours].title}</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">{workingHours[language as keyof typeof workingHours].weekdays}</span>
                              <span className="font-medium">{workingHours[language as keyof typeof workingHours].weekdaysTime}</span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-muted-foreground">{workingHours[language as keyof typeof workingHours].weekend}</span>
                              <span className="font-medium">{workingHours[language as keyof typeof workingHours].weekendTime}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              className="max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', stiffness: 300, damping: 35 }}
            >
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 mb-4">
                  <HelpCircle className="h-8 w-8 text-primary" />
                </div>
                <h2 className="text-4xl font-bold mb-4">
                  {language === 'fr' && 'Questions Fréquentes'}
                  {language === 'en' && 'Frequently Asked Questions'}
                  {language === 'es' && 'Preguntas Frecuentes'}
                  {language === 'zh' && '常见问题'}
                  {language === 'ar' && 'الأسئلة الشائعة'}
                </h2>
                <p className="text-muted-foreground text-lg">
                  {language === 'fr' && 'Trouvez des réponses aux questions les plus courantes sur nos services douaniers'}
                  {language === 'en' && 'Find answers to the most common questions about our customs services'}
                  {language === 'es' && 'Encuentre respuestas a las preguntas más comunes sobre nuestros servicios aduaneros'}
                  {language === 'zh' && '查找有关我们海关服务的最常见问题的答案'}
                  {language === 'ar' && 'ابحث عن إجابات للأسئلة الأكثر شيوعًا حول خدماتنا الجمركية'}
                </p>
              </div>

              <Card className="premium-shadow">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full">
                    {faqData.map((faq, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left">
                          {faq.question[language as keyof typeof faq.question]}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer[language as keyof typeof faq.answer]}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
